/**
 * The language menu.
 *
 * A native `<select>` on purpose: it is keyboard-accessible, screen-reader
 * correct and right-to-left aware for free, and on a phone it opens the OS
 * picker — all of which a hand-rolled dropdown would have to re-earn.
 *
 * Options are labelled with endonyms (Русский, العربية, 中文) because someone
 * looking for their own language recognises it written in itself.
 */
import { LOCALES, LOCALE_NAMES, type LocaleCode } from '../lib/i18n'
import { useLocale } from '../lib/i18n/I18nProvider'
import { useT } from '../lib/i18n/I18nProvider'

interface Props {
  /** `plan` when the choice edits a document, `app` when it is a preference. */
  variant?: 'app' | 'plan'
  /**
   * Runs *in addition to* switching the app's language. The editor uses it to
   * persist the choice onto the plan, since inside a plan the language is a
   * property of the document and decides what its PDF prints in.
   */
  onChange?: (locale: LocaleCode) => void
}

export function LanguagePicker({ variant = 'app', onChange }: Props) {
  const t = useT()
  const { locale, setLocale } = useLocale()

  const choose = (next: LocaleCode) => {
    setLocale(next)
    onChange?.(next)
  }

  return (
    <label className={`lang-picker lang-picker--${variant}`}>
      <GlobeIcon />
      <span className="sr-only">{t.common.language}</span>
      <select
        className="lang-picker-select"
        value={locale}
        aria-label={t.common.language}
        onChange={(event) => choose(event.target.value as LocaleCode)}
      >
        {LOCALES.map((code) => (
          <option key={code} value={code}>
            {LOCALE_NAMES[code]}
          </option>
        ))}
      </select>
    </label>
  )
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  )
}
